import { useApolloClient } from "@apollo/client";
import { CubeQuery } from "@models/ISchema";
import { useSettingsStore } from "@context/useSettingsStore";
import {
  beginInstructorRemovalVerification,
  isSameInstructorRemovalContext,
  setInstructorRemovalFlowState,
  useInstructorRemovalFlowStore,
} from "@context/instructorRemovalFlow/useInstructorRemovalFlowStore";
import {
  InstructorRemovalFlowContext,
  InstructorRemovalOutcome,
  InstructorRemovalSnapshot,
} from "@context/instructorRemovalFlow/types";
import { classifyInstructorRemoval } from "@context/instructorRemovalFlow/classifyInstructorRemoval";
import instructorRemovalSnapshotQuery from "../graphql/instructorRemovalSnapshot.query.graphql";

interface UseInstructorRemovalValidationProps {
  context: InstructorRemovalFlowContext;
  canCorrectInBundle: boolean;
}

const toSnapshot = (
  cube: CubeQuery | null | undefined,
  context: InstructorRemovalFlowContext,
): InstructorRemovalSnapshot => {
  const info = cube?.editorView?.info;
  if (info?.__typename !== "LinkInfo" || !info.link || String(info.link.id) !== context.linkId) {
    throw new Error("The affected league could not be loaded");
  }

  const link = info.link;
  return {
    context: { ...context, bundleId: link.bundle?.id ? String(link.bundle.id) : undefined },
    courseCanEdit: !!link.course?.edhCanEdit,
    instructors: (link.instructors ?? []).filter(Boolean).map(instructorLink => ({
      instructorLinkId: String(instructorLink.id),
      instructorId: String(instructorLink.instructor.id),
      responsibility: Number(instructorLink.teachingHoursPercentage ?? 0),
      isPrimary: !!instructorLink.isPrimary,
    })),
  };
};

export const useInstructorRemovalValidation = ({
  context,
  canCorrectInBundle,
}: UseInstructorRemovalValidationProps) => {
  const client = useApolloClient();
  const enabled = useSettingsStore(
    state => state.validateInstructorsAfterRemoval?.enabled === true,
  );

  const readSnapshot = async (): Promise<InstructorRemovalSnapshot> => {
    const { data } = await client.query<{ cube: CubeQuery }>({
      query: instructorRemovalSnapshotQuery,
      variables: {
        scenarioId: context.scenarioId,
        originId: context.originId,
        resourceId: context.linkId,
      },
      fetchPolicy: "no-cache",
    });

    return toSnapshot(data?.cube, context);
  };

  const prepareBeforeSave = async () => {
    if (!enabled) return true;

    const previous = useInstructorRemovalFlowStore.getState();
    const pendingReplacement =
      previous.pendingReplacement && isSameInstructorRemovalContext(previous.context, context);

    beginInstructorRemovalVerification(context, pendingReplacement);

    try {
      const beforeSnapshot = await readSnapshot();
      const activeFlow = useInstructorRemovalFlowStore.getState();
      if (!isSameInstructorRemovalContext(activeFlow.context, context)) return false;

      setInstructorRemovalFlowState({
        context: beforeSnapshot.context,
        beforeSnapshot,
        status: "saving-assignment",
      });
      return true;
    } catch (error) {
      console.error("[InstructorRemoval] Before-save snapshot failed", error);
      if (
        isSameInstructorRemovalContext(useInstructorRemovalFlowStore.getState().context, context)
      ) {
        setInstructorRemovalFlowState({ status: "pre-save-failed" });
      }
      return false;
    }
  };

  const verifySavedLeague = async (): Promise<InstructorRemovalOutcome | null> => {
    if (!enabled) return null;

    const flow = useInstructorRemovalFlowStore.getState();
    if (!flow.beforeSnapshot || !isSameInstructorRemovalContext(flow.context, context)) {
      setInstructorRemovalFlowState({ status: "verification-failed" });
      return null;
    }

    setInstructorRemovalFlowState({ status: "verifying-saved-league" });

    try {
      const afterSnapshot = await readSnapshot();
      const activeFlow = useInstructorRemovalFlowStore.getState();
      if (!isSameInstructorRemovalContext(activeFlow.context, context)) return null;

      const outcome = classifyInstructorRemoval(
        flow.beforeSnapshot,
        afterSnapshot,
        flow.pendingReplacement,
      );
      const nextContext = afterSnapshot.context;

      if (!outcome.reasons.length) {
        setInstructorRemovalFlowState({
          status: "idle",
          context: null,
          beforeSnapshot: null,
          afterSnapshot: null,
          reasons: [],
          pendingReplacement: false,
        });
        return outcome;
      }

      const effectiveCorrectionPermission =
        canCorrectInBundle && afterSnapshot.courseCanEdit && !!afterSnapshot.context.bundleId;
      const status = outcome.hasNoInstructors
        ? "needs-replacement"
        : effectiveCorrectionPermission
          ? "needs-bundle-correction"
          : "warning-only";

      setInstructorRemovalFlowState({
        status,
        context: nextContext,
        afterSnapshot,
        reasons: outcome.reasons,
      });
      return outcome;
    } catch (error) {
      console.error("[InstructorRemoval] Post-save verification failed", error);
      if (
        isSameInstructorRemovalContext(useInstructorRemovalFlowStore.getState().context, context)
      ) {
        setInstructorRemovalFlowState({ status: "verification-failed" });
      }
      return null;
    }
  };

  return {
    enabled,
    prepareBeforeSave,
    verifySavedLeague,
    retryVerification: verifySavedLeague,
  };
};
